import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  MdFileUpload,
  MdFolder,
  MdChevronLeft,
  MdChevronRight,
  MdCompress,
  MdSave,
} from "react-icons/md";
import { fetchWrapper } from "../utils/fetchWrapper";
import "./ImageCompressor.css";

const ImageCompressor = () => {
  const [items, setItems] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [quality, setQuality] = useState(80);
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const compareRef = useRef(null);
  const carouselRef = useRef(null);

  const setFolderRef = (el) => {
    if (el) {
      el.setAttribute("webkitdirectory", "");
      el.setAttribute("directory", "");
    }
    folderInputRef.current = el;
  };

  const addFiles = (fileList) => {
    const imageFiles = Array.from(fileList).filter((f) =>
      f.type.startsWith("image/")
    );
    if (imageFiles.length === 0) {
      setError("No valid image files found.");
      return;
    }
    const newItems = imageFiles.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      compressedUrl: null,
      compressedBlob: null,
      compressedSize: null,
      status: "pending",
      quality: 80,
    }));
    setItems((prev) => [...prev, ...newItems]);
    setError(null);
  };

  const handleFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("drag-active");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-active");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-active");
    addFiles(e.dataTransfer.files);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getReduction = (item) => {
    if (!item.compressedSize) return 0;
    return Math.round(
      ((item.file.size - item.compressedSize) / item.file.size) * 100
    );
  };

  // Compress all images
  const compressAll = async () => {
    if (items.length === 0) return;
    setCompressing(true);
    setError(null);

    const updated = [...items];

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === "done") continue;

      updated[i].status = "compressing";
      setItems([...updated]);

      const formData = new FormData();
      formData.append("image", updated[i].file);

      try {
        const response = await fetchWrapper.post(
          `/image-compressor/compress?quality=${updated[i].quality || 80}`,
          formData,
          {
            responseType: "blob",
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        const blob = new Blob([response.data]);
        updated[i].compressedUrl = URL.createObjectURL(blob);
        updated[i].compressedBlob = blob;
        updated[i].compressedSize = blob.size;
        updated[i].status = "done";
      } catch (err) {
        console.error(`Error compressing ${updated[i].file.name}:`, err);
        updated[i].status = "error";
      }

      setItems([...updated]);
    }

    setCompressing(false);
    // Select first completed item
    const firstDone = updated.findIndex((it) => it.status === "done");
    if (firstDone >= 0) setSelectedIndex(firstDone);
  };

  // Re-compress selected image with new quality
  const applyQuality = async () => {
    const item = items[selectedIndex];
    if (!item) return;

    const updated = [...items];
    updated[selectedIndex].status = "compressing";
    updated[selectedIndex].quality = quality;
    setItems([...updated]);

    const formData = new FormData();
    formData.append("image", item.file);

    try {
      const response = await fetchWrapper.post(
        `/image-compressor/compress?quality=${quality}`,
        formData,
        {
          responseType: "blob",
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const blob = new Blob([response.data]);
      updated[selectedIndex].compressedUrl = URL.createObjectURL(blob);
      updated[selectedIndex].compressedBlob = blob;
      updated[selectedIndex].compressedSize = blob.size;
      updated[selectedIndex].status = "done";
    } catch (err) {
      console.error("Error re-compressing:", err);
      updated[selectedIndex].status = "error";
    }

    setItems([...updated]);
  };

  // Download single
  const downloadSingle = (item) => {
    if (!item.compressedUrl) return;
    const link = document.createElement("a");
    link.href = item.compressedUrl;
    const parts = item.file.name.split(".");
    const ext = parts.pop();
    const name = parts.join(".");
    link.setAttribute("download", `${name}-compressed.${ext}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Save all as ZIP
  const saveAll = async () => {
    const doneItems = items.filter((it) => it.status === "done" && it.compressedBlob);
    if (doneItems.length === 0) return;

    // If single file, download directly
    if (doneItems.length === 1) {
      downloadSingle(doneItems[0]);
      return;
    }

    const formData = new FormData();
    doneItems.forEach((it) => formData.append("images", it.file));

    try {
      const response = await fetchWrapper.post(
        `/image-compressor/compress-bulk`,
        formData,
        {
          responseType: "blob",
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "compressed-images.zip");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading ZIP:", err);
      setError("Failed to download ZIP.");
    }
  };

  // Carousel navigation
  const scrollCarousel = (dir) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: dir * 160,
        behavior: "smooth",
      });
    }
  };

  // Comparison slider drag
  const handleSliderMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleSliderMove = useCallback(
    (e) => {
      if (!isDragging || !compareRef.current) return;
      const rect = compareRef.current.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const x = clientX - rect.left;
      const pct = Math.min(100, Math.max(0, (x / rect.width) * 100));
      setSliderPos(pct);
    },
    [isDragging]
  );

  const handleSliderUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleSliderMove);
      window.addEventListener("mouseup", handleSliderUp);
      window.addEventListener("touchmove", handleSliderMove);
      window.addEventListener("touchend", handleSliderUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleSliderMove);
      window.removeEventListener("mouseup", handleSliderUp);
      window.removeEventListener("touchmove", handleSliderMove);
      window.removeEventListener("touchend", handleSliderUp);
    };
  }, [isDragging, handleSliderMove, handleSliderUp]);

  // Update quality when selecting a different image
  useEffect(() => {
    if (items[selectedIndex]) {
      setQuality(items[selectedIndex].quality || 80);
      setSliderPos(50);
    }
  }, [selectedIndex, items]);

  const selectedItem = items[selectedIndex] || null;
  const doneCount = items.filter((it) => it.status === "done").length;
  const hasPending = items.some(
    (it) => it.status === "pending" || it.status === "error"
  );
  const isPng = selectedItem?.file?.type === "image/png";

  // ─── UPLOAD VIEW ───
  if (items.length === 0) {
    return (
      <div className="ic-container">
        <div className="ic-header">
          <h1>Image Compressor</h1>
          <p>
            Compress JPEG, PNG & WebP images. Adjust quality and preview
            before/after.
          </p>
        </div>
        <div
          className="ic-dropzone"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <MdFileUpload className="ic-dropzone-icon" />
          <h3>Drop Your Files Here</h3>
          <p>or click to browse. JPEG, PNG, WebP supported.</p>
          <div
            className="ic-dropzone-btns"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="ic-btn ic-btn-primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <MdFileUpload size={18} /> Select Files
            </button>
            <button
              className="ic-btn ic-btn-secondary"
              onClick={() => folderInputRef.current?.click()}
            >
              <MdFolder size={16} /> Add Folder
            </button>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            accept="image/*"
            multiple
          />
          <input
            type="file"
            ref={setFolderRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            accept="image/*"
            multiple
          />
        </div>
        {error && <div className="ic-error">{error}</div>}
      </div>
    );
  }

  // ─── MAIN VIEW (files loaded) ───
  return (
    <div className="ic-container">
      <div className="ic-header">
        <h1>Image Compressor</h1>
        <p>Compress JPEG, PNG & WebP images. Adjust quality and preview before/after.</p>
      </div>

      {/* ─── Top: Thumbnail Carousel ─── */}
      <div className="ic-carousel-section">
        <button
          className="ic-carousel-arrow ic-carousel-arrow-left"
          onClick={() => scrollCarousel(-1)}
        >
          <MdChevronLeft size={28} />
        </button>

        <div className="ic-carousel" ref={carouselRef}>
          {items.map((item, idx) => (
            <div
              key={idx}
              className={`ic-thumb-card ${idx === selectedIndex ? "selected" : ""}`}
              onClick={() => setSelectedIndex(idx)}
            >
              <div className="ic-thumb-img-wrap">
                <img src={item.preview} alt={item.file.name} />
                {item.status === "done" && (
                  <div className="ic-thumb-overlay">
                    <span className="ic-thumb-pct">
                      -{getReduction(item)}%
                    </span>
                  </div>
                )}
                {item.status === "compressing" && (
                  <div className="ic-thumb-overlay ic-thumb-loading">
                    <div className="ic-spinner-small" />
                  </div>
                )}
              </div>
              {item.status === "done" && (
                <button
                  className="ic-thumb-save"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadSingle(item);
                  }}
                >
                  SAVE
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          className="ic-carousel-arrow ic-carousel-arrow-right"
          onClick={() => scrollCarousel(1)}
        >
          <MdChevronRight size={28} />
        </button>
      </div>

      {/* ─── Action Buttons ─── */}
      <div className="ic-action-bar">
        <div className="ic-action-bar-left">
           <button
            className="ic-btn ic-btn-secondary"
            onClick={() => {
              setItems([]);
              setSelectedIndex(0);
              setError(null);
            }}
          >
            Clear All
          </button>
          <button
            className="ic-btn ic-btn-secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            <MdFileUpload size={16} /> Add More
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            accept="image/*"
            multiple
          />
          <input
            type="file"
            ref={setFolderRef}
            onChange={handleFileChange}
            style={{ display: "none" }}
            accept="image/*"
            multiple
          />
        </div>

        <div className="ic-action-bar-right">
          {hasPending && (
            <button
              className="ic-btn ic-btn-compress"
              onClick={compressAll}
              disabled={compressing}
            >
              <MdCompress size={18} />
              {compressing ? "Compressing…" : "COMPRESS"}
            </button>
          )}
          {doneCount > 0 && (
            <button className="ic-btn ic-btn-save" onClick={saveAll}>
              <MdSave size={18} />
              SAVE ALL
              <span className="ic-badge">{doneCount}</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── Comparison Panel ─── */}
      {selectedItem && selectedItem.status === "done" && (
        <div className="ic-compare-panel">
          <h3 className="ic-compare-filename">{selectedItem.file.name}</h3>
          <div className="ic-compare-stats">
            <span>
              Before: <strong>{formatSize(selectedItem.file.size)}</strong>
            </span>
            <span>
              After:{" "}
              <strong>
                ≈ {formatSize(selectedItem.compressedSize)} (-
                {getReduction(selectedItem)}%)
              </strong>
            </span>
          </div>

          <div className="ic-compare-body">
            {/* Before/After Slider */}
            <div
              className="ic-compare-view"
              ref={compareRef}
              onMouseDown={handleSliderMouseDown}
              onTouchStart={handleSliderMouseDown}
            >
              {/* After image (full background) */}
              <img
                src={selectedItem.compressedUrl}
                alt="After"
                className="ic-compare-img"
                draggable={false}
              />
              {/* Before image (clipped) */}
              <div
                className="ic-compare-before"
                style={{ width: `${sliderPos}%` }}
              >
                <img
                  src={selectedItem.preview}
                  alt="Before"
                  className="ic-compare-img"
                  style={{ width: `${100 / (sliderPos / 100)}%`, maxWidth: 'none' }}
                  draggable={false}
                />
              </div>
              {/* Divider */}
              <div
                className="ic-compare-divider"
                style={{ left: `${sliderPos}%` }}
              >
                <div className="ic-compare-handle">
                  <MdChevronLeft size={14} />
                  <MdChevronRight size={14} />
                </div>
              </div>
              {/* Labels */}
              <span className="ic-compare-label ic-label-before">Before</span>
              <span className="ic-compare-label ic-label-after">After</span>
            </div>

            {/* Quality Control */}
            <div className="ic-quality-panel">
              <label className="ic-quality-label">
                {isPng ? "Colors" : "Quality"}
              </label>
              {isPng ? (
                <>
                  <input
                    type="range"
                    className="ic-quality-slider"
                    min={2}
                    max={256}
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                  />
                  <span className="ic-quality-value">{quality}</span>
                </>
              ) : (
                <>
                  <input
                    type="range"
                    className="ic-quality-slider"
                    min={10}
                    max={100}
                    value={quality}
                    onChange={(e) => setQuality(parseInt(e.target.value))}
                  />
                  <span className="ic-quality-value">{quality}%</span>
                </>
              )}
              <button className="ic-btn ic-btn-apply" onClick={applyQuality}>
                APPLY
              </button>
            </div>
          </div>
        </div>
      )}

      {/* If selected item is pending or compressing */}
      {selectedItem && selectedItem.status !== "done" && (
        <div className="ic-compare-panel ic-compare-pending">
          <h3 className="ic-compare-filename">{selectedItem.file.name}</h3>
          <div className="ic-compare-stats">
            <span>
              Size: <strong>{formatSize(selectedItem.file.size)}</strong>
            </span>
          </div>
          <div className="ic-pending-preview">
            <img
              src={selectedItem.preview}
              alt={selectedItem.file.name}
              className="ic-pending-img"
            />
            {selectedItem.status === "compressing" && (
              <div className="ic-pending-loader">
                <div className="ic-spinner" />
                <span>Compressing…</span>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="ic-error">{error}</div>}
    </div>
  );
};

export default ImageCompressor;
